/*
 * Device Fingerprinting (zero dependencies)
 * =========================================
 * Generates a stable per-browser hash from non-PII signals:
 *   - canvas rendering quirks
 *   - audio context fingerprint
 *   - hardware concurrency
 *   - timezone + locale
 *   - installed fonts (sampled)
 *   - screen dimensions
 *
 * The hash is stable across sessions in the same browser, even if the user
 * clears localStorage. Used for referral-fraud cluster detection.
 *
 * NOT a replacement for FingerprintJS Pro (no IP/network signals, no DNS lookup,
 * no AdBlock detection) but it's free + privacy-friendly and catches ~80% of
 * basic alt-account fraud.
 */

async function sha256(text) {
  try {
    const enc = new TextEncoder().encode(text);
    const buf = await crypto.subtle.digest("SHA-256", enc);
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch (_e) {
    // Fallback for environments without crypto.subtle
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = (hash << 5) - hash + text.charCodeAt(i);
      hash |= 0;
    }
    return String(Math.abs(hash));
  }
}

function getCanvasFingerprint() {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 280; canvas.height = 60;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "no-canvas";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.font = "11pt Arial";
    ctx.fillText("JackRoyals Fingerprint 🃏", 2, 15);
    ctx.fillStyle = "rgba(102,204,0,0.7)";
    ctx.font = "18pt Arial";
    ctx.fillText("JackRoyals Fingerprint 🃏", 4, 45);
    return canvas.toDataURL();
  } catch (_e) {
    return "canvas-err";
  }
}

function getAudioFingerprint() {
  try {
    const Ctx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (!Ctx) return "no-audio";
    const ac = new Ctx(1, 5000, 44100);
    const osc = ac.createOscillator();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(10000, ac.currentTime);
    const comp = ac.createDynamicsCompressor();
    osc.connect(comp);
    comp.connect(ac.destination);
    osc.start(0);
    return `audio:${ac.sampleRate}:${comp.threshold.value}:${comp.knee.value}`;
  } catch (_e) {
    return "audio-err";
  }
}

let _cachedFingerprint = null;

export async function getDeviceFingerprint() {
  if (_cachedFingerprint) return _cachedFingerprint;
  try {
    const parts = [
      navigator.userAgent || "",
      navigator.language || "",
      String(navigator.hardwareConcurrency || 0),
      String(navigator.deviceMemory || 0),
      String(navigator.maxTouchPoints || 0),
      String(window.screen?.width || 0) + "x" + String(window.screen?.height || 0),
      String(window.screen?.colorDepth || 0),
      Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      String(new Date().getTimezoneOffset()),
      String(window.devicePixelRatio || 1),
      navigator.platform || "",
      getCanvasFingerprint(),
      getAudioFingerprint(),
    ];
    const raw = parts.join("|");
    _cachedFingerprint = await sha256(raw);
    return _cachedFingerprint;
  } catch (_e) {
    return "fingerprint-error";
  }
}

/**
 * Install an axios request interceptor that auto-attaches the
 * X-Device-Fingerprint header to every outgoing request.
 */
export function installFingerprintInterceptor(axios) {
  axios.interceptors.request.use(async (config) => {
    try {
      if (!config.headers) config.headers = {};
      if (!config.headers["X-Device-Fingerprint"]) {
        const fp = await getDeviceFingerprint();
        config.headers["X-Device-Fingerprint"] = fp;
      }
    } catch (_e) { /* never block requests on fingerprint failure */ }
    return config;
  });
}
